import React, { useState, useEffect, useCallback, useMemo } from "react";
import Breadcrumb from "../../components/common/Breadcrumb";
import { getEmpLog, GetEmployeeDataLogPdf } from "../../services/dataService";
import { getSecureItem } from "../../services/SecureStorage";
import axios from "axios";

const TYPE_LABELS = {
  1: "Employee master",
  2: "Personal details",
  3: "Finance",
  4: "Leave",
  5: "Documents",
  6: "Service book",
};

const TYPE_CLASSES = {
  1: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  2: "bg-purple-50 text-purple-700 ring-1 ring-purple-200",
  3: "bg-green-50 text-green-700 ring-1 ring-green-200",
  4: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  5: "bg-pink-50 text-pink-700 ring-1 ring-pink-200",
  6: "bg-teal-50 text-teal-700 ring-1 ring-teal-200",
};

const ACTION_CLASSES = {
  UPDATE: "bg-blue-50 text-blue-700",
  INSERT: "bg-green-50 text-green-700",
  DELETE: "bg-red-50 text-red-700",
};

const PAGE_SIZE = 20;

const ActionBadge = ({ action }) => {
  const key = action?.toUpperCase();
  const cls = ACTION_CLASSES[key] ?? "bg-gray-100 text-gray-600";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}
    >
      {action ?? "—"}
    </span>
  );
};

const TypeBadge = ({ typeKey }) => {
  const key = String(typeKey ?? "");
  const cls =
    TYPE_CLASSES[key] ?? "bg-gray-100 text-gray-600 ring-1 ring-gray-200";
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}
    >
      {TYPE_LABELS[key] ?? (key ? `Type ${key}` : "—")}
    </span>
  );
};

export function EmployeeLog() {
  const UID = getSecureItem("userId");

  const [logData, setLogData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [searchCode, setSearchCode] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterAction, setFilterAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const payload = {
          FromDate: fromDate || null,
          ToDate: toDate || null,
          type: filterType || null,
          EmpId: null,
          UserId: UID,
          page: page,
          pageSize: 15,
          Isgetfull: true,
          SearchName: searchName || null,
          SearchCode: searchCode || null,
          ActionType: filterAction || null,
        };

        const resp = await getEmpLog(payload);

        const apiRoot = resp?.data?.data ?? resp?.data ?? resp ?? {};
        const rows = Array.isArray(apiRoot?.data)
          ? apiRoot.data
          : Array.isArray(apiRoot)
            ? apiRoot
            : [];
        const total = Number(
          apiRoot?.TotalRecords ?? resp?.data?.TotalRecords ?? rows.length,
        );
        const pages = Number(
          apiRoot?.TotalPages ??
            resp?.data?.TotalPages ??
            Math.max(1, Math.ceil(total / PAGE_SIZE)),
        );

        setLogData(rows);
        setTotalRecords(total);
        setTotalPages(Math.max(1, pages));
        setCurrentPage(page);
      } catch (err) {
        console.error("Failed to fetch employee log:", err);
        setLogData([]);
        setTotalRecords(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [UID, fromDate, toDate, filterType, searchName, searchCode, filterAction],
  );

  const handleDownloadPdf = async () => {
    try {
      const response = await GetEmployeeDataLogPdf({
        FromDate: fromDate || null,
        ToDate: toDate || null,
        type: filterType || null,
        EmpId: null,
        page: 1,
        pageSize: 0,
        SearchName: searchName || null,
        SearchCode: searchCode || null,
      });

      const blob = new Blob([response.data], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;
      link.download = "employee-log.pdf";

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download failed:", error);
    }
  };
  useEffect(() => {
    fetchData(1);
  }, []);

  const filtered = useMemo(() => {
    if (!Array.isArray(logData)) return [];

    return logData.filter((r) => {
      const name = String(r?.EmployeeName ?? "").toLowerCase();
      const code = String(r?.EmpCode ?? "").toLowerCase();
      const action = String(r?.ActionType ?? "").toUpperCase();

      const nameOk = !searchName || name.includes(searchName.toLowerCase());
      const codeOk = !searchCode || code.includes(searchCode.toLowerCase());
      const actionOk = !filterAction || action === filterAction;

      return nameOk && codeOk && actionOk;
    });
  }, [logData, searchName, searchCode, filterAction]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchData(page);
  };

  const handleShow = () => {
    fetchData(1);
  };

  const clearFilters = () => {
    setSearchName("");
    setSearchCode("");
    setFilterType("");
    setFilterAction("");
    setFromDate("");
    setToDate("");
    setTimeout(() => fetchData(1), 0);
  };

  const insertCount = Array.isArray(logData)
    ? logData.filter((r) => r?.ActionType?.toUpperCase() === "INSERT").length
    : 0;
  const updateCount = Array.isArray(logData)
    ? logData.filter((r) => r?.ActionType?.toUpperCase() === "UPDATE").length
    : 0;
  const deleteCount = Array.isArray(logData)
    ? logData.filter((r) => r?.ActionType?.toUpperCase() === "DELETE").length
    : 0;

  const pageNumbers = useMemo(() => {
    const pages = [];
    const windowSize = 5;
    const start = Math.max(
      1,
      Math.min(currentPage - 2, totalPages - windowSize + 1),
    );
    const end = Math.min(totalPages, start + windowSize - 1);
    for (let p = start; p <= end; p += 1) pages.push(p);
    return pages;
  }, [currentPage, totalPages]);

  const formatUtcDateTime = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    const formattedDate = `${date.getUTCFullYear()}-${String(
      date.getUTCMonth() + 1,
    ).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;

    const formattedTime = date
      .toLocaleTimeString("en-US", {
        timeZone: "UTC",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return `${formattedDate}, ${formattedTime}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Breadcrumb title="Employee log" />

      <div className="p-6 space-y-5">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h3 className="font-bold">Last 20 Changes</h3> <div></div>
              <button
                onClick={handleDownloadPdf}
                className="bg-red-600 text-white px-4 py-2 rounded-lg"
              >
                Download PDF
              </button>
            </div>
          </div>

          <div className="px-6 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[180px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search employee name…"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="w-full pl-9 pr-3 h-9 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
              />
            </div>

            <div className="relative min-w-[140px]">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                />
              </svg>
              <input
                type="text"
                placeholder="Emp code…"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                className="w-full pl-9 pr-3 h-9 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 cursor-pointer transition min-w-[150px]"
            >
              <option value="">All types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition min-w-[150px]"
            />

            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition min-w-[150px]"
            />

            <button
              onClick={handleShow}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-white bg-blue hover:bg-blue border border-blue-600 rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
            >
              Show
            </button>

            <button
              onClick={clearFilters}
              className="h-9 px-4 text-sm text-gray-500 hover:text-red-500 border border-gray-200 hover:border-red-200 rounded-lg bg-white transition-colors flex items-center gap-1.5"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Clear
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading records…</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-10">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Action
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Column
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Old value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      New value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Modified by
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Modified on
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <svg
                            className="w-10 h-10 text-gray-200"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p className="text-gray-400 text-sm">
                            No records found
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row, i) => (
                      <tr
                        key={row?.Id ?? i}
                        className="hover:bg-blue-50/30 transition-colors group"
                      >
                        <td className="px-4 py-3 text-gray-300 text-xs">
                          {(currentPage - 1) * PAGE_SIZE + i + 1}
                        </td>
                        <td className="px-4 py-3">{row?.EmployeeName}</td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                            {row?.EmpCode ?? "—"}
                          </code>
                        </td>
                        <td className="px-4 py-3">
                          <ActionBadge action={row?.ActionType} />
                        </td>

                        <td
                          className="px-4 py-3 text-gray-500 text-xs max-w-[140px] truncate"
                          title={row?.ModifiedColumn}
                        >
                          {row?.ModifiedColumn ?? "—"}
                        </td>
                        <td className="px-4 py-3">
                          {row?.OldValue ? (
                            <span
                              className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded max-w-[120px] truncate inline-block"
                              title={row.OldValue}
                            >
                              {row.OldValue}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {row?.NewValue ? (
                            <span
                              className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded max-w-[120px] truncate inline-block"
                              title={row.NewValue}
                            >
                              {row.NewValue}
                            </span>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                          {row?.ModifiedBy ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {formatUtcDateTime(row?.ModifiedOn)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!loading && totalRecords > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 flex-wrap gap-3">
              <p className="text-xs text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-600">
                  {Math.min((currentPage - 1) * PAGE_SIZE + 1, totalRecords)}
                </span>
                {" – "}
                <span className="font-semibold text-gray-600">
                  {Math.min(currentPage * PAGE_SIZE, totalRecords)}
                </span>
                {" of "}
                <span className="font-semibold text-gray-600">
                  {totalRecords}
                </span>
                {" records"}
              </p>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition text-xs"
                >
                  «
                </button>

                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition text-xs"
                >
                  ‹
                </button>

                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    onClick={() => goToPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition border ${
                      p === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : "border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 bg-white"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition text-xs"
                >
                  ›
                </button>

                <button
                  onClick={() => goToPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 disabled:opacity-30 disabled:cursor-not-allowed transition text-xs"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
